"use client";

import { useDeferredValue, useMemo, useState } from "react";
import Link from "next/link";
import { ArrowUpRight, MapPin, Search } from "@/components/icons";

import {
  buildCityPageUrl,
  canBuildCityPageUrl,
} from "@/lib/listings/build-geo-url";
import { formatNumber } from "@/lib/formatting/numbers";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";

export interface CitySearchItem {
  id: string;
  name: string;
  slug: string;
  listingCount?: number;
}

interface CitySearchSectionProps {
  cities: CitySearchItem[];
  productSlug: string;
  stateSlug: string;
  title?: string;
  featuredCount?: number;
  maxResults?: number;
  className?: string;
  preserveOrder?: boolean;
}

interface CityRowProps {
  city: CitySearchItem;
  href: string;
  maxCount: number;
  showIcon?: boolean;
  showArrow?: boolean;
  flushBorder?: boolean;
}

function CityRowCount({
  count,
  showArrow,
}: {
  count: number;
  showArrow: boolean;
}) {
  return (
    <span className="relative z-10 flex shrink-0 items-center gap-2 tabular-nums text-xs text-muted-foreground">
      {count > 0 ? formatNumber(count) : "—"}
      {showArrow && (
        <ArrowUpRight
          aria-hidden
          className="h-3 w-3 text-muted-foreground/70 transition-colors group-hover:text-foreground"
        />
      )}
    </span>
  );
}

function CityRow({
  city,
  href,
  maxCount,
  showIcon = false,
  showArrow = true,
  flushBorder = false,
}: CityRowProps) {
  const count = city.listingCount ?? 0;
  const intensity =
    maxCount > 0 ? Math.max(0.04, Math.min(1, count / maxCount)) : 0;
  const barWidth = `${Math.round(intensity * 100)}%`;

  return (
    <Link
      href={href}
      prefetch={false}
      className={cn(
        "group relative flex h-full items-center justify-between gap-3 overflow-hidden px-3 py-2 text-sm transition-colors hover:bg-muted/60",
        !flushBorder && "border-b border-border last:border-b-0",
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 bg-brand-tint opacity-50 transition-[width,opacity] group-hover:opacity-100"
        style={{ width: barWidth }}
      />
      <span className="relative z-10 flex min-w-0 items-center gap-2">
        {showIcon && (
          <MapPin
            aria-hidden
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
          />
        )}
        <span className="truncate font-medium text-foreground">
          {city.name}
        </span>
      </span>
      <CityRowCount count={count} showArrow={showArrow} />
    </Link>
  );
}

export default function CitySearchSection({
  cities,
  productSlug,
  stateSlug,
  title = "Shop by City",
  featuredCount = 15,
  maxResults = 60,
  className,
  preserveOrder = false,
}: CitySearchSectionProps) {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);

  const validCities = useMemo(() => {
    const filtered = cities.filter((city) =>
      canBuildCityPageUrl(productSlug, stateSlug, city.slug),
    );
    if (preserveOrder) return filtered;
    return filtered.slice().sort((left, right) => {
      const countDiff = (right.listingCount ?? 0) - (left.listingCount ?? 0);
      if (countDiff !== 0) return countDiff;
      return left.name.localeCompare(right.name);
    });
  }, [cities, productSlug, stateSlug, preserveOrder]);

  const featured = validCities.slice(0, featuredCount);
  const featuredMax = Math.max(0, ...featured.map((city) => city.listingCount ?? 0));
  const remainingCities = validCities.slice(featuredCount);
  const validCitiesMax = Math.max(0, ...validCities.map((city) => city.listingCount ?? 0));

  const trimmed = deferredQuery.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!trimmed) return [];
    return validCities
      .filter((city) => city.name.toLowerCase().includes(trimmed))
      .slice(0, maxResults);
  }, [validCities, trimmed, maxResults]);

  const searchResultsMax = useMemo(
    () =>
      searchResults.reduce(
        (max, city) => Math.max(max, city.listingCount ?? 0),
        0,
      ),
    [searchResults],
  );

  if (validCities.length === 0) return null;

  const isSearching = trimmed.length > 0;
  const cityCountLabel = validCities.length === 1 ? "city" : "cities";

  return (
    <section className={cn("w-full", className)}>
      <h2 className="mb-3 text-overline text-muted-foreground uppercase">
        {title}
      </h2>

      <div className="relative mb-3">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${formatNumber(validCities.length)} ${cityCountLabel}…`}
          aria-label="Search cities"
          className="h-10 w-full rounded-sm border border-border bg-card pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0"
        />
      </div>

      {isSearching ? (
        searchResults.length === 0 ? (
          <Card className="border-dashed px-4 py-6 text-center" padding="none">
            <p className="text-sm text-muted-foreground">
              No cities match &ldquo;{deferredQuery.trim()}&rdquo;.
            </p>
          </Card>
        ) : (
          <ul
            aria-label="Search results"
            className="overflow-hidden rounded-sm border border-border bg-card"
          >
            {searchResults.map((city) => (
              <li key={city.id}>
                <CityRow
                  city={city}
                  href={buildCityPageUrl(productSlug, stateSlug, city.slug)}
                  maxCount={searchResultsMax}
                  showIcon
                />
              </li>
            ))}
          </ul>
        )
      ) : (
        featured.length > 0 && (
          <Card className="overflow-hidden" padding="none">
            <ul className="-mb-px -mr-px grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {featured.map((city) => (
                <li
                  key={city.id}
                  className="-mb-px -mr-px border-b border-r border-border"
                >
                  <CityRow
                    city={city}
                    href={buildCityPageUrl(productSlug, stateSlug, city.slug)}
                    maxCount={featuredMax}
                    flushBorder
                  />
                </li>
              ))}
            </ul>
          </Card>
        )
      )}

      {remainingCities.length > 0 && (
        <details className="mt-3 overflow-hidden rounded-sm border border-border bg-card">
          <summary className="cursor-pointer list-none px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/60">
            Browse all {formatNumber(validCities.length)} {cityCountLabel}
          </summary>
          <ul
            aria-label={`All ${cityCountLabel}`}
            className="border-t border-border"
          >
            {remainingCities.map((city) => (
              <li key={city.id}>
                <CityRow
                  city={city}
                  href={buildCityPageUrl(productSlug, stateSlug, city.slug)}
                  maxCount={validCitiesMax}
                  showArrow={false}
                />
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
